/**
 * Subscription Adapters
 *
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 *
 * Adapters for Stripe subscription data transformation.
 *
 * FCR S291 Issue 9 (DD-3): Stripe field reads use the dual-path helpers
 * in ./_stripe-helpers.ts so the adapter works against both legacy
 * (≤2025-12) and API 2026-04-22.dahlia shapes. Direct ``line.price.product``
 * / ``order.payment_intent`` reads return null under v15 and would surface
 * "No license key" on every subscription card.
 */

import { TSL_TRIAL_PRODUCT } from "@/lib/constants";
import i18n from "@/i18n";
import { getTslFeaturesToStripe, getTslPaidProducts } from "@/services/productIdConfig";
import {
  countDistinctProducts,
  filterLicensesByProduct,
  filterLicensesByPriceId,
} from "@/lib/license-matching";
import { formatCurrency, formatDate, getProductDisplayName } from "@/lib/utils";
import { ordersApi } from "@/redux/services/orders";
import { store, type AppDispatch } from "@/redux/store";
import { License, StripeCustomer } from "@/types";
import {
  fetchLicense,
  fetchLicenses,
  fetchProductName,
} from "@/utils/dashboardUtils";
import { logger } from "@/lib/logger";
import type { ActiveSubscription, SubscriptionProduct, Product } from "./adapter-types";
import { getPurchasedDeviceCount } from "@/lib/licensing";
import {
  getInvoicePaymentIntent,
  getLineProductId,
  getLineInterval,  // FCR S523 Issue 4: needed for synthetic row display name
  getLineType, // SU-29 / G-3 (FCR S557): classify the refunded entitlement
} from "./_stripe-helpers";
// N-10 (S629): formatNumber reads the shop's active language. A bare
// toLocaleString() follows the BROWSER's language instead, so one page
// could show translated words around numbers grouped for a different
// country than the one the customer chose.
import { readInvoicePresentment } from "@/adapters/_invoice-presentment";

/**
 * FCR S523 Issue 4: Shared helper for building the display name, interval, and
 * amount for a synthetic "Past Subscription" / "Past Product" row.
 *
 * Active subscription rows route through fetchProductName() + getProductDisplayName()
 * and render correctly. Synthetic rows for refunded products previously read
 * matchingLine.description directly — a Stripe-formatted receipt string like
 * "1 × Dictation Pro v2 (at $69.00 / year)" — instead of the clean product name.
 *
 * This helper routes synthetic rows through the same naming pipeline so every
 * refunded row shows "Dictation Pro v2 - Annual License" with the correct badge
 * and price, regardless of which invoice it came from.
 *
 * Used in both synthetic-row sites (buildSyntheticRefundedSubscriptionRows for
 * Billing and adaptSubscriptionProducts for My Products) so they can never drift.
 */
async function resolveSyntheticProductDisplay(
  pid: string,
  matchingLine: { description?: string; amount?: number } | undefined,
  currency: string,
): Promise<{ productName: string; interval: string; amount: number; amountDisplay: string }> {
  const lineInterval = getLineInterval(matchingLine as Parameters<typeof getLineInterval>[0]) ?? "";
  const baseName = await fetchProductName(pid);
  const productName = getProductDisplayName(
    baseName ?? "Subscription",
    lineInterval ? "recurring" : undefined,
    lineInterval || undefined,
    pid,
  );
  const amount = (matchingLine as { amount?: number } | undefined)?.amount ?? 0;
  return {
    productName,
    interval: lineInterval,
    amount,
    amountDisplay: amount > 0 ? formatCurrency(currency, amount) : "",
  };
}

export const adaptActiveSubscription = async (
  stripeCustomer: StripeCustomer,
  orders?: unknown[],
): Promise<ActiveSubscription[]> => {
  // S-3 (FCR S509 Pass 10): a customer with zero remaining Stripe
  // subscriptions (e.g. their only subscription product was refunded) must
  // still see a synthetic Past Subscription row for that refund -- so this
  // early return now falls through to the synthetic-row builder below
  // instead of returning [] immediately.
  const hasNoLiveSubscriptions =
    !stripeCustomer ||
    !stripeCustomer.subscriptions ||
    !stripeCustomer.subscriptions.data ||
    stripeCustomer.subscriptions.data.length === 0;

  if (hasNoLiveSubscriptions) {
    logger.info("No subscriptions found for customer - checking for refunded products only");
    return buildSyntheticRefundedSubscriptionRows([], orders);
  }

  const getPaymentIntent = async (
    productId: string,
    subscriptionId: string,
  ) => {
    // SK-2 (FCR S323): Two-pass search that always prefers the subscription_create
    // invoice.  QLM stores licenses under the payment intent from the original
    // checkout invoice (billing_reason: "subscription_create").  After any renewal
    // Stripe creates a new invoice (billing_reason: "subscription_cycle") with a
    // different payment intent.  Stripe returns invoices newest-first, so the single-
    // pass loop used to return the renewal PI — causing "No license key" on the
    // dashboard for every subscriber after their first renewal.
    try {
      const ordersQuery = ordersApi.endpoints.getOrders.initiate();
      const ordersResult = await (store.dispatch as AppDispatch)(ordersQuery);
      const orders = ordersResult.data;

      if (!orders || !Array.isArray(orders)) {
        return "";
      }

      let fallback = "";

      for (const order of orders) {
        if (!order?.lines?.data || !Array.isArray(order.lines.data)) {
          continue;
        }

        for (const line of order.lines.data) {
          // FCR S291 Issue 9: dual-path productId resolution. Direct
          // line.price.product is null under v15.
          const lineProductId = getLineProductId(line);
          if (!lineProductId || !line?.parent) {
            continue;
          }

          if (
            lineProductId === productId &&
            line.parent.subscription_item_details?.subscription_item ===
              subscriptionId
          ) {
            // FCR S291 Issue 9: order.payment_intent is null under v15;
            // walk the new payments.data[0].payment.payment_intent path.
            const pi = getInvoicePaymentIntent(order) || "";
            if (order.billing_reason === "subscription_create") {
              // Always prefer the original checkout invoice — QLM licensed under this PI.
              return pi;
            }
            if (!fallback) {
              // Keep first renewal PI as last-resort (e.g. migrated subscriptions
              // with no subscription_create invoice in the returned history).
              fallback = pi;
            }
          }
        }
      }

      return fallback;
    } catch (error) {
      logger.error("Error fetching payment intent:", error);
    }

    return "";
  };

  // Defensive optional chaining: the hasNoLiveSubscriptions early-return above
  // already guarantees subscriptions.data is non-empty at runtime by this
  // point, but TypeScript's control-flow narrowing does not carry through an
  // intermediate boolean variable (hasNoLiveSubscriptions), so this guards
  // against TS18048 without changing behavior.
  const subscriptionPromises = (stripeCustomer.subscriptions?.data ?? []).map(
    (subscription) =>
      Promise.all(
        subscription.items.data.map(async (item) => {
          // S-12 (FCR S323): Use item subtotal × effective tax rate rather than the
          // full latest_invoice.amount_paid. When a customer buys an annual subscription
          // alongside another product in the same checkout, amount_paid reflects the
          // combined invoice total — inflating the "Next Payment" widget beyond the actual
          // subscription renewal amount. item.price.unit_amount × quantity is the correct
          // subscription-only base; then we scale by the effective tax rate from that same
          // invoice so tax-jurisdiction customers see a tax-inclusive renewal estimate.
          const itemSubtotal =
            (item.price.unit_amount ?? 0) * (item.quantity ?? 1);

          let chargedAmount = itemSubtotal;

          const inv = subscription.latest_invoice;
          const invSubtotal = inv?.subtotal ?? 0;
          const invTax = inv?.tax ?? 0;
          if (invSubtotal > 0 && invTax > 0) {
            const effectiveTaxRate = invTax / invSubtotal;
            chargedAmount = Math.round(itemSubtotal * (1 + effectiveTaxRate));
          }

          if (chargedAmount === 0) {
            logger.warn(
              `[subscription-adapter] chargedAmount is 0 for ` +
                `subscription ${subscription.id} / item ${item.id}. ` +
                `Check that price.unit_amount is set on the Stripe item.`,
            );
          }

          const itemPaymentIntent = await getPaymentIntent(
            item.price.product,
            item.id,
          );
          // R2-2 C: Stripe keeps the subscription "active" because QLM expiry is a
          // manual server-side operation invisible to Stripe. Cross-reference the
          // QLM license state so the row can show "Expired" and suppress Cancel.
          //
          // S-2 (FCR S509 Pass 10): use .every(), not .some(). A multi-product
          // subscription sharing one payment intent can return more than one
          // license under fetchLicense -- after a per-product refund revokes
          // the refunded product's key, .some() would mark the KEPT product
          // expired too, just because its expired sibling shows up in the
          // same lookup. A product is genuinely expired only when every
          // license resolved for it is expired.
          let qlmExpired = false;
          try {
            const lic = await fetchLicense(
              itemPaymentIntent,
              item.price.product,
            );
            const itemLicenses = Array.isArray(lic) ? lic : [];
            qlmExpired =
              itemLicenses.length > 0 &&
              itemLicenses.every(
                (l) =>
                  Boolean(l.IsExpired) ||
                  l.ActivationStatus?.toLowerCase().includes("expired") === true,
              );
          } catch (error) {
            logger.error(
              `Failed to fetch QLM license state for item ${item.id}:`,
              error,
            );
          }

          // N-13 (S652): the subscription's own start, not the current period's.
          // After the first renewal current_period_start is the RENEWAL date, and
          // this value is rendered under the heading "Purchased" on My Products.
          // The fallback keeps a subscription whose start date the API did not
          // return rendering exactly as it did before.
          const startedAt = subscription.start_date ?? item.current_period_start ?? 0;

          return {
            id: item.id,
            renewalDate: formatDate(item.current_period_end),
            // N-13 (S652): the purchase date, not the current period's start.
            startDate: formatDate(startedAt),
            // N-13 (S652): raw Unix seconds for the newest-first sort below.
            startedAt,
            // A-4: raw Unix seconds; the CURRENT period's start, which is the
            // renewal date once a subscription has renewed at least once. Kept
            // as its own field so nothing has to infer one from the other.
            currentPeriodStart: item.current_period_start ?? 0,
            active: item.plan.active,
            amountDisplay: formatCurrency(
              item.price.currency,
              chargedAmount,
            ),
            amount: chargedAmount,
            currency: item.price.currency,
            // SU-5 / N-13 (S663): the currency the renewals will be converted
            // into. Recorded by Stripe on the CHARGE and persisted by this shop
            // onto the invoice, as display_currency or as the
            // presentment_currency metadata the checkout and renewal handlers
            // write. It is NOT a field on the Subscription -- an earlier round
            // proposed `subscription.presentment_details` and that field does
            // not exist, so the line would have rendered for nobody.
            //
            // `latest_invoice` is already expanded on the profile response, so
            // this needs no server change and no extra request. `resolved`
            // false means the invoice recorded nothing, which is the ordinary
            // case for a dollar-billed customer -- and the row then omits the
            // line rather than naming the integration currency back at them.
            presentmentCurrency: (() => {
              const p = readInvoicePresentment(subscription.latest_invoice);
              return p.resolved ? p.currency : null;
            })(),
            productId: item.price.product,
            // S256 (FCR §3): route productName through getProductDisplayName
            // so the Subscriptions row shows the same suffix as every other
            // surface. Subscriptions are always `type: "recurring"` by
            // definition; interval comes from the Stripe plan object.
            // SS-6 (FCR Pass 14): Derive a priceLabel from the price nickname
            // or word_count metadata so TSL subscriptions show the full plan
            // name (e.g. "Text Speaker Live v4 - 45,000 Words - Monthly")
            // rather than the bare product name. All three dashboard surfaces
            // (Overview, Next Payment, License Details) read from this adapter,
            // so fixing it here corrects all three at once.
            // NOTE: priceLabel is computed with await before the object literal
            // (extracted below) to avoid assigning a Promise<string> to
            // productName, which would happen if await were used inside a
            // non-async IIFE. The value is resolved here before the object spread.
            productName: await (async () => {
              const baseProductName = await fetchProductName(item.price.product);
              const wordCountMeta = (item.plan as { metadata?: Record<string, string> })?.metadata?.word_count;
              const wordCountLabel = wordCountMeta
                ? i18n.t("common.wordCountTitle", {
                    count: Number(wordCountMeta),
                  })
                : "";
              const planNickname = (item.plan as { nickname?: string })?.nickname?.trim() ?? "";
              const priceLabel = planNickname
                ? planNickname
                : wordCountLabel
                  ? `${baseProductName} - ${wordCountLabel}`
                  : "";
              return getProductDisplayName(
                baseProductName,
                "recurring",
                item.plan.interval,
                item.price.product,
                priceLabel || undefined,
              );
            })(),
            paymentIntent: itemPaymentIntent,
            qlmExpired,
            interval: item.plan.interval,
            quantity: item.quantity ?? 1,
            status: subscription.status,
            cancelAtPeriodEnd: subscription.cancel_at_period_end,
            // S347: The Stripe Subscription ID (sub_xxx) is required by the
            // Customer Portal subscription_cancel flow. `id` above is the
            // SubscriptionItem ID (si_xxx) — a different object entirely.
            subscriptionId: subscription.id,
            // The Stripe Price ID for this item. Used by adaptSubscriptionProducts
            // to group rows, so two line items for one product at different
            // prices stay on separate cards.
            priceId: item.price.id,
            // Bug 3 (FCR Pass 6): pass raw Unix timestamps; do not format here.
            // The portal often sets cancel_at without setting cancel_at_period_end,
            // so detection logic in subscription-row must inspect both fields.
            // canceledAt distinguishes "scheduled" from "already ended".
            cancelAt: subscription.cancel_at ?? null,
            canceledAt: subscription.canceled_at ?? null,
            // SK-2 (FCR Pass 8): Flag upgrade-driven cancellations so the
            // Reactivate button is hidden. The marker is tsl_plan_upgrade, set
            // by the 45K→100K PU-1 cancel (S3 fix): the cancellation was
            // intentional and resuming billing would create a double-charge.
            cancelledDueToUpgrade:
              subscription.metadata?.["tsl_plan_upgrade"] === "true",
          };
        }),
      ),
  );

  const nestedSubscriptions = await Promise.all(subscriptionPromises);
  const liveRows = nestedSubscriptions.flat();

  // S-3 (FCR S509 Pass 10): append a synthetic Past Subscription row for
  // each refunded product not already represented by a live row above.
  const syntheticRows = await buildSyntheticRefundedSubscriptionRows(liveRows, orders);

  return [...liveRows, ...syntheticRows];
};

/**
 * S-3 (FCR S509 Pass 10): Build synthetic "Past Subscription" rows for
 * products that were refunded on an invoice but no longer have a live
 * Stripe subscription representing them on the Billing page.
 *
 * Mirrors the same synthetic-row pattern already used by
 * ``adaptSubscriptionProducts`` for the My Products page (see
 * ``syntheticRefundedRows`` below) -- but built against ``ActiveSubscription``'s
 * shape instead of ``Product``'s, since the Billing page's
 * ``ActiveSubscriptionsTable`` consumes ``adaptActiveSubscription``'s output
 * directly, not the My Products adapter.
 *
 * Root cause this closes: per-product refunds on a multi-product invoice
 * (S-2) can leave the refunded product's Stripe subscription item deleted
 * (not cancelled) with no trace in ``stripeCustomer.subscriptions`` at all.
 * Without a synthetic row, the customer has no record the product was ever
 * billed -- it simply vanishes from Billing. A refunded product on a one-time
 * invoice never had a Stripe subscription to begin with and has the same
 * problem.
 */
/**
 * FCR Pass 13 Sakshi Issue 2: Deduplication key for refunded past rows.
 *
 * Previous code keyed on product ID alone, which caused a refunded row to be
 * suppressed whenever the same product was repurchased (both share the same
 * productId, so the second live purchase "covered" the old refunded one).
 *
 * The correct key is product + payment intent (one purchase). A new purchase
 * has a different PI, so it no longer erases the refund history of the old one.
 * When no PI is available (edge case: very old invoices), fall back to
 * product + invoice creation timestamp as a stable surrogate.
 */
function refundedPastRowKey(productId: string, paymentIntent: string, invoiceCreated = 0): string {
  if (paymentIntent) return `${productId}:${paymentIntent}`;
  return `${productId}:inv-${invoiceCreated}`;
}

/**
 * Build the set of already-covered refunded-purchase keys from live rows so
 * we only suppress a synthetic row when the *exact same purchase* (same
 * productId + paymentIntent) is already represented by a canceled Stripe row.
 * A new active purchase of the same product does NOT cover the old refunded one.
 */
function buildCoveredRefundedPastKeys(liveRows: ActiveSubscription[]): Set<string> {
  const keys = new Set<string>();
  for (const row of liveRows) {
    // Only canceled Stripe subscription rows represent prior purchases --
    // active rows are the new purchase and must not suppress refund history.
    if (row.status !== "canceled" && !row.qlmExpired) continue;
    if (!row.paymentIntent) continue;
    keys.add(refundedPastRowKey(row.productId, row.paymentIntent));
  }
  return keys;
}

async function buildSyntheticRefundedSubscriptionRows(
  liveRows: ActiveSubscription[],
  orders?: unknown[],
): Promise<ActiveSubscription[]> {
  if (!orders || !Array.isArray(orders) || orders.length === 0) return [];

  // FCR Pass 13 Sakshi Issue 2: dedupe by product+paymentIntent (purchase level),
  // not just by productId. A new active repurchase no longer hides old refund history.
  const coveredKeys = buildCoveredRefundedPastKeys(liveRows);
  // Still track productId+invoicePI combos we've emitted to avoid duplicate synthetic rows
  // when the same product is refunded on more than one invoice.
  const emittedKeys = new Set<string>();

  // Gather all products that need a synthetic row before resolving names, so we
  // can run the async display lookups in parallel (same pattern as active rows).
  const pending: {
    pid: string;
    paymentIntent: string;
    matchingLine: { description?: string; amount?: number } | undefined;
    createdAt: number;
    currency: string;
    /**
     * SU-29 / G-3 (FCR S557): whether the refunded entitlement was a
     * subscription. A one-time entitlement never becomes a subscription row.
     */
    isRecurring: boolean;
  }[] = [];

  // H-4 (FCR S557): an invoice line this adapter cannot classify leaves the
  // account with nothing recording that the purchase existed, and a fault that
  // records nothing stays invisible for as long as nobody happens to look.
  //
  // Two shapes are possible on this path and both were silent: a refunded
  // product with no matching invoice line at all, and a matching line that
  // getLineType cannot classify. Either way the row below falls back to
  // subscription framing, so the customer sees a refunded purchase described as
  // an expired subscription with nothing anywhere saying the classification was
  // a guess.
  //
  // Collected across the whole pass and reported once, so a genuinely unusual
  // invoice does not fill the log.
  const unclassifiedRefunds: string[] = [];

  for (const inv of orders) {
    const invoice = inv as {
      id?: string;
      refunded_product_ids?: string[];
      lines?: { data?: unknown[] };
      created?: number;
      currency?: string;
      payment_intent?: string;
      payments?: { data?: Array<{ payment?: { payment_intent?: string } }> };
    };
    const refundedPids = invoice.refunded_product_ids ?? [];
    if (!Array.isArray(refundedPids) || refundedPids.length === 0) continue;

    // Read the invoice payment intent to form a purchase-level key.
    const invoicePI = getInvoicePaymentIntent(invoice) || "";
    const createdAt = invoice.created ?? 0;
    const lines: unknown[] = invoice.lines?.data ?? [];

    for (const pid of refundedPids) {
      const purchaseKey = refundedPastRowKey(pid, invoicePI, createdAt);
      // Skip if this exact purchase is already represented by a Stripe row.
      if (coveredKeys.has(purchaseKey)) continue;
      // Skip if we already added a synthetic row for this purchase this pass.
      if (emittedKeys.has(purchaseKey)) continue;
      emittedKeys.add(purchaseKey);

      const matchingLine = lines.find((l) => {
        const line = l as {
          price?: { product?: string };
          pricing?: { price_details?: { product?: string } };
        };
        return (
          line?.price?.product === pid ||
          line?.pricing?.price_details?.product === pid
        );
      }) as { description?: string; amount?: number } | undefined;

      // SU-29 / G-3 (FCR S557): classify the entitlement here, where the row is
      // built, rather than filtering one-time products out of the subscriptions
      // view afterwards. A refunded one-time purchase is not a subscription, it
      // cannot expire, and it must never acquire subscription framing --
      // filtering it later would hide the symptom and leave the wrong
      // classification available to every other view.
      const refundedLineType = getLineType(matchingLine as Parameters<typeof getLineType>[0]);

      // H-4 (FCR S557): record, do not reclassify. The fallback below is
      // unchanged deliberately -- with no line to read there is nothing better
      // to decide from, and moving a refunded row to a different tab on the
      // strength of a guess would trade a visible wrong answer for an invisible
      // one. What was missing is any record that the answer was guessed.
      if (!matchingLine) {
        unclassifiedRefunds.push(
          `${pid} (no matching line on invoice ${invoice.id ?? "unknown"})`,
        );
      } else if (refundedLineType === undefined) {
        unclassifiedRefunds.push(
          `${pid} (line present but unclassifiable on invoice ${invoice.id ?? "unknown"})`,
        );
      }

      pending.push({
        pid,
        paymentIntent: invoicePI,
        matchingLine,
        createdAt,
        currency: invoice.currency ?? "usd",
        isRecurring: refundedLineType !== "one_time",
      });
    }
  }

  // H-4 (FCR S557): one record per load, naming the products, so a support call
  // about a refunded purchase shown in the wrong place has something to read.
  if (unclassifiedRefunds.length > 0) {
    logger.warn(
      `[Subscription] ${unclassifiedRefunds.length} refunded product(s) could not be ` +
        `classified from their invoice line and default to subscription framing: ` +
        unclassifiedRefunds.join(", "),
    );
  }

  // FCR Pass 13 Sakshi Issue 2 (additional): resolve display info in parallel,
  // matching the approach already used for active subscription rows.
  const resolved = await Promise.all(
    pending.map((x) => resolveSyntheticProductDisplay(x.pid, x.matchingLine, x.currency)),
  );

  const syntheticRows: ActiveSubscription[] = pending.flatMap((x, i) => {
    const { productName, interval, amount, amountDisplay } = resolved[i];

    // SU-29 / G-3 (FCR S557): only recurring entitlements produce a subscription
    // row. A refunded one-time purchase carries no renewal and no expiry, so
    // presenting it under Past Subscriptions described something that cannot
    // happen. The invoice and its refund stay visible in invoice history
    // permanently -- the entitlement is removed, the financial record is not.
    if (!x.isRecurring && !interval) {
      return [];
    }

    return [{
      id: `refunded-${x.pid}-${x.paymentIntent || x.createdAt}`,
      renewalDate: "",
      startDate: x.createdAt ? formatDate(x.createdAt) : "",
      interval,
      active: false,
      amountDisplay,
      amount,
      currency: x.currency,
      // Store the paymentIntent so dedup logic in future passes can use it.
      paymentIntent: x.paymentIntent,
      productId: x.pid,
      productName,
      // status === "canceled" routes this to Past Subscriptions in the Billing
      // table. qlmExpired true and active false reflect that the license was
      // revoked by the refund.
      status: "canceled",
      qlmExpired: true,
    }];
  });

  return syntheticRows;
}

export const adaptSubscriptionProducts = async (
  activeSubscriptions: ActiveSubscription[],
  orders?: unknown[],
): Promise<SubscriptionProduct[]> => {
  if (!activeSubscriptions || activeSubscriptions.length === 0) {
    return [];
  }

  // CHANGE_06 (FCR S483 Bugs 2-3): Split into active and cancelled subscriptions.
  // Previously cancelled subs were filtered out entirely (S477) leaving the customer
  // with no record on the Products tab after cancellation — they had to check
  // Billing to understand why the product disappeared. Now cancelled subscriptions
  // produce a Product entry with displayStatus="expired" so the customer always
  // sees what they had and can use the "Buy again" link.
  const billableSubscriptions = activeSubscriptions.filter(
    (sub) => sub.status !== "canceled",
  );
  const cancelledSubscriptions = activeSubscriptions.filter(
    (sub) => sub.status === "canceled",
  );

  if (billableSubscriptions.length === 0 && cancelledSubscriptions.length === 0) {
    return [];
  }

  // NF-3: Group by productId:priceId so two line items for one product at
  // different prices (different tiers, or the same product bought twice on
  // different prices) stay on separate cards rather than merging into one.
  const groups = new Map<string, ActiveSubscription[]>();
  for (const sub of billableSubscriptions) {
    const priceId = (sub as ActiveSubscription & { priceId?: string }).priceId || "";
    const groupKey = priceId ? `${sub.productId}:${priceId}` : sub.productId;
    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey)!.push(sub);
  }

  // N-13 (S652): newest first, so group[0] really is the most recent purchase --
  // the card takes its id, its interval and its date from it. Nothing sorted
  // these before, so group[0] was whichever row Stripe happened to return first
  // while the comment below claimed otherwise.
  for (const items of groups.values()) {
    items.sort((x, y) => (y.startedAt ?? 0) - (x.startedAt ?? 0));
  }

  const productPromises = Array.from(groups.entries()).map(async ([groupKey, group]) => {
    const productId = group[0].productId;
    // VD-2 / GP-7: the group key distinguishes two rows of one product. It was
    // built and then discarded. Carrying it onto the row is what lets a consumer
    // address one row rather than one product -- the licence dialog was keyed on
    // the product, so opening it from one row found the other row's keys.
    const rowPriceId =
      (group[0] as ActiveSubscription & { priceId?: string }).priceId || "";

    const licensesNested: License[][] = await Promise.all(
      group.map(async (item: ActiveSubscription) => {
        try {
          const licenses = await fetchLicense(item.paymentIntent, productId);
          return Array.isArray(licenses) ? licenses : [];
        } catch (error) {
          logger.error(
            `Failed to fetch license for payment intent ${item.paymentIntent}:`,
            error,
          );
          return [];
        }
      }),
    );

    const productName = group[0].productName;
    let totalDevices = 0;
    let availableDevices = 0;

    // Build per-license quantity map before flattening.
    // group[i] is the subscription purchase that generated licensesNested[i].
    // Using group[0].quantity for all licenses is incorrect when a user has
    // multiple purchases of the same product with different quantities.
    // The TSL trial path below has its own quantity logic and is unaffected.
    const licenseQuantityMap = new Map<string, number>();
    licensesNested.forEach((licensesForPurchase, i) => {
      const qty = group[i].quantity ?? 1;
      licensesForPurchase.forEach((l) => {
        if (l.ActivationKey) licenseQuantityMap.set(l.ActivationKey, qty);
      });
    });

    // Handle TSL trial product separately
    if (productId === TSL_TRIAL_PRODUCT) {
      try {
        const allLicenses: License[] = await fetchLicenses();
        const matchResult = filterLicensesByProduct(allLicenses, productId);
        const licenses = matchResult.licenses;

        if (matchResult.legacyMatchCount > 0) {
          logger.info(
            `[TSL Trial] ${matchResult.legacyMatchCount} legacy license(s) matched by ProductID`,
          );
        }

        licenses.forEach((license) => {
          // SU-12 (FCR S557): purchased quantity, never the raw QLM seat count.
          const purchased = getPurchasedDeviceCount(license);
          totalDevices += purchased;
          // SU-13 (FCR S557): AvailableLicenses counts the reinstall buffer seat,
          // so availability is derived from the purchased quantity instead.
          availableDevices += Math.max(
            0,
            purchased - (license.ActivationCount ?? 0),
          );
        });

        // SU-10 (FCR S557): the Overview reads this figure.
        const totalLicensesCount = licenses.reduce(
          (sum, license) => sum + getPurchasedDeviceCount(license),
          0,
        );

        return {
          id: group[0].id,
          rowKey: groupKey,
          productId: productId,
          productName: productName,
          // S657, "from N purchases": purchases, not keys — see adapter-types.ts.
          purchaseCount: group.length,
          licenses: licenses,
          totalLicenses: totalLicensesCount,
          interval: group[0].interval,
          totalDevices: totalDevices,
          availableDevices: availableDevices,
        };
      } catch (error) {
        logger.error(
          `Failed to process TSL trial product ${productId}:`,
          error,
        );
        return {
          id: group[0].id,
          rowKey: groupKey,
          productId: productId,
          productName: productName,
          licenses: [],
          totalLicenses: 0,
          interval: group[0].interval,
          totalDevices: 0,
          availableDevices: 0,
        };
      }
    }

    // Process regular subscriptions - combine licenses from ALL purchases
    const flatLicenses = licensesNested.flat();
    // EX-2 (S589): the guard inside filterLicensesByProduct must see a count of
    // products. Counting licences meant a customer holding two keys for one
    // product tripped a guard meant for multi-product orders.
    const totalProductsInOrder = countDistinctProducts(flatLicenses);
    let matchResult = filterLicensesByProduct(flatLicenses, productId, totalProductsInOrder);
    let licenses = matchResult.licenses;

    // SK-1 (S306): TSL plan-upgrade fallback.
    // After a 45K→100K upgrade, the QLM record's OrderID/AffiliateID is set at
    // original purchase and intentionally not updated on upgrade — so the
    // upgrade-invoice paymentIntent returns 0 rows. Fall back to all-user-licenses
    // and re-match by Features field (the authoritative post-upgrade plan-tier indicator).
    // Safe: TSL_FEATURES_TO_PRODUCT_MAP keys are product-specific, so a 100K license
    // ("0:4;") will never match a 45K product card.
    if (licenses.length === 0 && getTslPaidProducts().has(productId)) {
      try {
        const allLicenses = await fetchLicenses();
        const fallbackResult = filterLicensesByProduct(allLicenses, productId);
        if (fallbackResult.licenses.length > 0) {
          matchResult = fallbackResult;
          licenses = fallbackResult.licenses;
          logger.info(
            `[Subscription ${productId}] TSL upgrade fallback: found ${licenses.length} license(s) via all-user fetch`,
          );
        }
      } catch (error) {
        logger.error(`[Subscription ${productId}] TSL upgrade fallback failed:`, error);
      }
    }

    // If no matching licenses found from payment-intent-scoped fetch, show 0
    // rather than widening the search to ALL user licenses via fetchLicenses().
    // The wider search caused cross-order contamination — licenses from
    // completely unrelated past orders could appear under the wrong product.
    if (licenses.length === 0 && flatLicenses.length > 0) {
      logger.warn(
        `[Subscription ${productId}] No matching licenses from payment intent ` +
          `(${flatLicenses.length} fetched, 0 matched). ` +
          `Check UserData1 population in QLM.`,
      );
    }

    if (matchResult.legacyMatchCount > 0) {
      logger.info(
        `[Subscription ${productId}] ${matchResult.legacyMatchCount} legacy license(s) matched by ProductID`,
      );
    }

    // VD-2 (Pass 20, corrected S656): narrow to this row's price BEFORE the
    // seat figures are summed. VD-2 narrowed the licence list the dialog reads
    // and left the three numbers printed beside it summed over both rows, so on
    // the one shape this filter exists for -- two line items of one product at
    // two prices in one order -- each row printed the other row's seats as well
    // as its own. The card then read "1 license key, 100 devices" for a
    // fifty-seat purchase, which is the SU-14 contradiction with the numbers
    // swapped. Every figure the row shows now comes from the same list.
    const rowLicenses = filterLicensesByPriceId(licenses, rowPriceId);

    rowLicenses.forEach((license) => {
      // SU-12 (FCR S557): purchased quantity, never the raw QLM seat count.
      // Where QLM reports no seat data, the Stripe quantity is the purchased
      // figure directly -- no buffer has been applied to it.
      const stripeQty = licenseQuantityMap.get(license.ActivationKey) ?? 1;
      const purchased =
        license.MaxActivations != null || license.NumLicenses != null
          ? getPurchasedDeviceCount(license)
          : Math.max(1, stripeQty);
      totalDevices += purchased;
      // SU-13 (FCR S557): AvailableLicenses counts the reinstall buffer seat.
      availableDevices += Math.max(
        0,
        purchased - (license.ActivationCount ?? 0),
      );
    });

    // SU-10 (FCR S557): the Overview reads this figure. It shows what the
    // customer bought, not the internal seat count.
    const totalLicensesCount = rowLicenses.reduce((sum, license) => {
      const qty = licenseQuantityMap.get(license.ActivationKey) ?? 1;
      return (
        sum +
        (license.MaxActivations != null || license.NumLicenses != null
          ? getPurchasedDeviceCount(license)
          : Math.max(1, qty))
      );
    }, 0);

    return {
      id: group[0].id,
      // VD-2: addresses this row, not this product.
      rowKey: groupKey,
      productId: productId,
      productName: productName,
      // S657, "from N purchases": purchases, not keys — see adapter-types.ts.
      purchaseCount: group.length,
      // VD-2: only this card's licences, so two rows of one product do not each
      // show the other's keys -- and the same list the seat figures above were
      // summed from.
      licenses: rowLicenses,
      totalLicenses: totalLicensesCount,
      interval: group[0].interval,
      totalDevices: totalDevices,
      availableDevices: availableDevices,
      // N-13 (S652): the PURCHASE date as Unix seconds -- the subscription's own
      // start, not the current period's. My Products renders this through
      // formatPurchaseDate() under the heading "Purchased", so reading
      // currentPeriodStart here made every renewed subscription misstate the day
      // it was bought. The group is sorted newest-first above, so group[0] is
      // genuinely the most recent purchase.
      createdAt: group[0].startedAt ?? 0,
    };
  });

  const result = await Promise.all(productPromises);

  // Issue 1 (FCR Pass 11): Cross-card TSL dedupe — must run BEFORE SK-2 suppress.
  // Both TSL paid plans share QLM ProductID 22, so the matching layer can route a
  // license to the wrong card when StripeProductId is absent or stale. This pass
  // removes any license whose Features tier does not match the card it landed on,
  // then recomputes device totals so the card counts are accurate.
  // Non-TSL products are untouched (the Features lookup returns undefined for them).
  const deduped = result.map((card) => {
    if (!getTslPaidProducts().has(card.productId)) return card;
    const filteredLicenses = card.licenses.filter((license) => {
      const tier = license.Features
        ? getTslFeaturesToStripe()[license.Features.trim()]
        : undefined;
      if (tier !== undefined) return tier === card.productId; // TSL: tier decides
      // License has no Features — keep it on this card as a safe fallback.
      return true;
    });
    if (filteredLicenses.length === card.licenses.length) return card;
    // Recompute device totals from the filtered list so counts stay accurate.
    let totalDevices = 0;
    let availableDevices = 0;
    for (const lic of filteredLicenses) {
      // SU-12 / SU-13 (FCR S557): purchased quantity on both figures.
      const purchased = getPurchasedDeviceCount(lic);
      const activated = Number(lic.ActivationCount) || 0;
      totalDevices += purchased;
      availableDevices += Math.max(0, purchased - activated);
    }
    return {
      ...card,
      licenses: filteredLicenses,
      totalLicenses: filteredLicenses.length,
      totalDevices,
      availableDevices,
    };
  });

  // SK-2 (S306): Suppress empty TSL product cards that appear after a plan upgrade.
  // After a 45K→100K upgrade, Stripe preserves the prior subscription history so
  // both a 45K and 100K card are created from the grouper. Once SK-1 routes the
  // license to the 100K card, the 45K card renders empty. Suppress it — but only
  // when at least one other TSL card has licenses, so the page never goes fully blank.
  // Run on the deduped result so a card emptied by dedupe is also caught here.
  const tslCards = deduped.filter((c) => getTslPaidProducts().has(c.productId));
  const populatedTslCount = tslCards.filter((c) => c.licenses.length > 0).length;
  let activeResult: SubscriptionProduct[];
  if (populatedTslCount > 0) {
    activeResult = deduped.filter(
      (c) => !getTslPaidProducts().has(c.productId) || c.licenses.length > 0,
    );
  } else {
    activeResult = deduped;
  }

  // CHANGE_06 (FCR S483 Bugs 2-3): Build lightweight expired entries for
  // cancelled subscriptions so they appear on the Products tab with a status
  // label and a "Buy again" link. QLM licences are revoked so we don't attempt
  // to fetch them — the entry is display-only with empty license arrays.
  const expiredEntries: Product[] = cancelledSubscriptions
    .filter(
      (sub) =>
        // Deduplicate: skip if an active entry already covers this product+price.
        !activeResult.some(
          (r) =>
            r.productId === sub.productId &&
            (r as SubscriptionProduct & { priceId?: string }).priceId ===
              ((sub as ActiveSubscription & { priceId?: string }).priceId || ""),
        ),
    )
    .map((sub): Product => {
      // S658: `accessEndDate` used to be built here, formatted in a fixed
      // "en-US" with an English month name, carried through product-adapter and
      // declared on the Product type -- and read by nothing. No screen has ever
      // rendered it: the expired subscription row on Billing & Payments draws
      // its own date from `renewalDate`, which is the true end of service, and
      // the My Products card shows the "expired" badge and no date. It is
      // removed rather than translated, because polishing a value no customer
      // can see is how it survives another five readings. If My Products should
      // one day name the day access ended, that is a new decision about what
      // the card says, not a field to resurrect.
      return {
        id: sub.id,
        productId: sub.productId,
        productName: sub.productName || "Subscription",
        licenses: [],
        totalLicenses: 0,
        type: "recurring",
        interval: sub.interval || "",
        totalDevices: 0,
        availableDevices: 0,
        // sub.createdAt never existed on ActiveSubscription -- the real Unix
        // timestamp is carried through as startedAt (set when this object was
        // built in adaptActiveSubscription above), smuggled past the public
        // interface the same way the My Products adapter already does a few
        // lines above this one.
        //
        // N-13 (S652): startedAt, not currentPeriodStart, for the same reason
        // the active rows use it -- this value is read as a purchase date.
        createdAt: sub.startedAt ?? sub.currentPeriodStart ?? 0,
        displayStatus: "expired",
        // S-2 / SU-4 (FCR Pass 14): Carry paymentIntent so the dedup key
        // productId+paymentIntent matches the synthetic refunded row.
        // Without this the guard in buildMyProductsCoveredKeys() skips every
        // expired Stripe row (all have no paymentIntent) and the synthetic
        // row is emitted alongside it -- producing two cards per product.
        paymentIntent: sub.paymentIntent || undefined,
      };
    });

  // SU-23 (FCR S637): My Products no longer builds rows for refunded purchases.
  //
  // This block used to scan every invoice for refunded_product_ids and ADD a row
  // for each one, marked displayStatus "expired". The view model only recognises
  // a refund when the status says "refunded", so a refunded product arrived as
  // "expired", was not recognised as refunded, and was rendered with the numeric
  // zeros an expired-but-real entitlement would show: no licence keys, "0/0
  // Devices", a disabled View License button and no refunded label. The row told
  // the customer their purchase was broken rather than refunded.
  //
  // Removal is the design, not decoration. The specification -- quoted correctly
  // in the review and written in this codebase as a comment for two sessions --
  // is that a refunded purchase is no longer an entitlement and must not appear
  // on My Products at all. The financial record stays: the invoice and its
  // refund remain in Billing and Payments permanently, which is where a customer
  // and Support both need them.
  //
  // The refunded products are still collected, but only to REMOVE matching rows
  // (item 116) and, as a safety net, to correct the status of any row that
  // survives (item 117) so the view model shows the refunded label and an em
  // dash rather than "0/0". The safety net is a fallback; removal is the design.
  /**
   * The PURCHASES that were refunded -- product AND payment, not product alone.
   *
   * SU-23 (S637 follow-up): this used to be a set of product ids, filled from
   * every invoice and then used to drop every row carrying a matching id. A
   * refund is an event on ONE purchase, so a customer who refunded a product in
   * January and bought it again in March lost the March subscription from My
   * Products -- a live, paying subscription with no card, no licence key and no
   * way to reach it. The dedup keys three lines below already said what the
   * right unit was ("a new repurchase of the same product is active, so it will
   * NOT cover the old refunded purchase"); the removal simply was not using them.
   */
  const refundedPurchaseKeys = new Set<string>();
  /**
   * Products EVERY purchase of which was refunded.
   *
   * A row that carries no payment intent cannot be matched to a purchase, and
   * both possible errors there are real: keeping a refunded row shows the
   * customer "0/0 Devices" on something they no longer own, and removing it on a
   * product-id guess hides a live subscription. This set is the case where there
   * is no guess to make -- nothing of that product survives, so whatever the row
   * describes, it was refunded.
   */
  const fullyRefundedProductIds = new Set<string>();
  if (orders && Array.isArray(orders)) {
    const purchasesByProduct = new Map<string, number>();
    const refundsByProduct = new Map<string, number>();
    // S655: products whose refund was recorded on an invoice with no readable
    // line. Collected here and judged after every invoice has been read -- see
    // the note at the decision below.
    const refundsWithNoReadableLine = new Set<string>();
    for (const inv of orders) {
      const invoice = inv as {
        refunded_product_ids?: string[];
        lines?: { data?: unknown[] };
      };
      const refundedIds = new Set(
        (invoice.refunded_product_ids ?? []).filter(Boolean),
      );
      const seenOnThisInvoice = new Set<string>();
      for (const line of invoice.lines?.data ?? []) {
        const productId = getLineProductId(line as never) as string | undefined;
        if (!productId || seenOnThisInvoice.has(productId)) continue;
        seenOnThisInvoice.add(productId);
        purchasesByProduct.set(
          productId,
          (purchasesByProduct.get(productId) ?? 0) + 1,
        );
        if (refundedIds.has(productId)) {
          refundsByProduct.set(
            productId,
            (refundsByProduct.get(productId) ?? 0) + 1,
          );
        }
      }
      // A refund recorded against a product with no readable line still counts:
      // the invoice says it happened, and no line means no purchase to compare
      // it against.
      for (const productId of refundedIds) {
        if (!seenOnThisInvoice.has(productId)) {
          refundsWithNoReadableLine.add(productId);
        }
      }
    }
    // S655: judged here rather than inside the loop above, where it asked
    // "have I seen a purchase of this product YET" -- a question whose answer
    // depends on the order Stripe happens to list the invoices in. Reading an
    // unreadable refund before the purchase it should have been weighed against
    // found no purchase, declared the product fully refunded, and removed a
    // live subscription from My Products; reading the same two invoices the
    // other way round kept it. The rule is about the whole account, so it can
    // only be settled once the whole account has been read.
    for (const productId of refundsWithNoReadableLine) {
      if (!purchasesByProduct.has(productId)) {
        fullyRefundedProductIds.add(productId);
      }
    }
    for (const [productId, refundCount] of refundsByProduct) {
      const purchaseCount = purchasesByProduct.get(productId) ?? 0;
      if (purchaseCount > 0 && refundCount >= purchaseCount) {
        fullyRefundedProductIds.add(productId);
      }
    }

    // FCR Pass 13 Sakshi Issue 2: My Products path — same purchase-level dedup fix.
    // Build the covered-key set from active and expired entries. A new repurchase
    // of the same product is active, not canceled, so it will NOT cover the old
    // refunded purchase.
    const buildMyProductsCoveredKeys = (): Set<string> => {
      const keys = new Set<string>();
      for (const r of [...activeResult, ...expiredEntries]) {
        const rr = r as { productId?: string; paymentIntent?: string; displayStatus?: string };
        if (!rr.paymentIntent || !rr.productId) continue;
        // Only expired/canceled entries represent a prior purchase that is already shown.
        if (rr.displayStatus !== "expired") continue;
        keys.add(refundedPastRowKey(rr.productId, rr.paymentIntent));
      }
      return keys;
    };
    const coveredKeys = buildMyProductsCoveredKeys();
    const emittedKeys = new Set<string>();

    // SU-23 item 115 (S637): the scan below no longer BUILDS rows. It records
    // which purchases were refunded, and that set is used to remove rows and to
    // correct the status of anything that survives.
    //
    // Resolving a display name, an interval and an amount for a row nobody
    // renders is work with no reader, so the resolution that used to happen
    // here is gone with the rows it fed. The dedup keys are kept: they are what
    // stops the same refunded purchase being counted twice when a customer has
    // repurchased the product since.
    for (const inv of orders) {
      const invoice = inv as {
        refunded_product_ids?: string[];
        created?: number;
        payment_intent?: string;
        payments?: { data?: Array<{ payment?: { payment_intent?: string } }> };
      };
      const refundedPids = invoice.refunded_product_ids ?? [];
      if (!Array.isArray(refundedPids) || refundedPids.length === 0) continue;

      const invoicePI = getInvoicePaymentIntent(invoice) || "";
      const createdAt = invoice.created ?? 0;

      for (const pid of refundedPids) {
        const purchaseKey = refundedPastRowKey(pid, invoicePI, createdAt);
        if (coveredKeys.has(purchaseKey)) continue;
        if (emittedKeys.has(purchaseKey)) continue;
        emittedKeys.add(purchaseKey);
        // Both spellings, because a row carries a payment intent and this
        // invoice may not: the row is matched on whichever the pair share.
        refundedPurchaseKeys.add(refundedPastRowKey(pid, invoicePI));
        refundedPurchaseKeys.add(purchaseKey);
      }
    }
  }

  // SU-23 items 116-117 (S637): remove the rows a refund has emptied, and
  // correct the status of anything that still gets through.
  //
  // A refund-driven cancelled or expired entry describes a purchase the customer
  // no longer holds, so it is dropped. The safety net exists because a row can
  // reach here from more than one source: if any does survive, it is marked
  // "refunded" rather than "expired", which is what makes the view model show
  // the refunded label, an em dash instead of "0/0" and a refund-specific
  // tooltip -- instead of a row that reads as a broken entitlement.
  const ownedRows = [...activeResult, ...expiredEntries].filter((row) => {
    // Typed with the field's own union rather than widened to `string`. The
    // widening is what let a comparison against "active" -- a value this field
    // can never hold -- compile and read as a safety check for a whole session.
    const { productId, paymentIntent } = row as {
      productId?: string;
      paymentIntent?: string;
      displayStatus?: Product["displayStatus"];
    };
    if (!productId) return true;
    // SU-7 (S652): nothing of this product survived any refund, so whatever
    // payment intent the row happens to carry, it describes an entitlement the
    // customer no longer holds.
    //
    // Purchase-level matching below stays the primary rule and is the right
    // unit -- it is what stops a repurchase being removed along with the
    // refunded original. It only works when both sides can name the same
    // purchase, and for a subscription they often cannot: the row's payment
    // intent comes from the subscription's own latest invoice while the set is
    // keyed on the refunded invoice's, so a fully refunded monthly product fell
    // between this rule and the safety net below and came out as a row with no
    // keys and "0/0 Devices".
    //
    // What makes this safe is the SET, and only the set. `displayStatus` is
    // typed "refunded" | "expired" | undefined -- undefined MEANS active, and
    // the field never holds the string "active" -- so a `displayStatus !==
    // "active"` test that stood here was true for every row that ever reached
    // it, including live ones. It read as the safety property and was not one.
    // It is removed rather than corrected to `!== undefined`, because that
    // would be a different rule from the one this adapter is tested for: a row
    // whose product has NO surviving unrefunded purchase describes an
    // entitlement the customer no longer holds, whatever the subscription
    // object still says, and the test "removes a row that carries no payment
    // intent when nothing of it survives" asserts exactly that on a live row.
    //
    // The protection a repurchase actually relies on is that its own
    // unrefunded invoice keeps the product OUT of fullyRefundedProductIds --
    // so a refund on the original can never remove it.
    if (fullyRefundedProductIds.has(productId)) {
      return false;
    }
    // Nothing left to match on: the rule above has already answered for every
    // product with no surviving purchase, and a row must never be dropped on a
    // product-id guess while an unrefunded purchase of it exists.
    if (!paymentIntent) return true;
    return !refundedPurchaseKeys.has(
      refundedPastRowKey(productId, paymentIntent),
    );
  });

  // The safety net, item 117: a row the removal above did not drop, whose
  // product has been fully refunded, is labelled "refunded" rather than left
  // reading "0/0 Devices" -- a state a customer reads as breakage. An ACTIVE
  // row is never relabelled: a live subscription must never be told it was
  // refunded.
  //
  // It is a net and not a rule. Removal is the design and this is the fallback
  // behind it, which is the order the specification sets.
  //
  // S660 pinned down exactly how far behind it: SU-7 above removes on the SAME
  // set and this branch also requires a productId, so as the two stand today
  // the net CANNOT fire -- every row it would relabel has already been dropped
  // one block up. That was established by mutation rather than by reading:
  // making this branch return the row unchanged leaves all 4,480 front-end
  // tests green, which is not a hole in the suite but a demonstration that the
  // branch is unreachable while the removal rule above it stands. (The previous
  // wording here said it "fires only for a row that reaches here carrying a
  // status the removal rule declined to act on", which described a path that
  // does not exist: the removal rule declines only when there is no productId,
  // and this branch requires one.)
  //
  // It is kept rather than deleted because item 117 specifies the label for a
  // row that does reach here, and the removal rule above is free to narrow
  // later. A net whose value is that it never fires is what a net is; if the
  // removal rule is ever changed, this is what stops a fully refunded product
  // reading "0/0 Devices" instead of "Refunded".
  const withSafetyNet = ownedRows.map((row) => {
    const { productId, paymentIntent } = row as {
      productId?: string;
      paymentIntent?: string;
      displayStatus?: Product["displayStatus"];
    };
    // M-D (S652): only when NOTHING of this product survived.
    //
    // This used to ask an account-wide question -- "has this product ever been
    // refunded here" -- which is weaker than the one the label needs. A product
    // refunded once and bought again still has a live purchase, and a canceling
    // annual subscription is not active, so both were relabelled "Refunded" on
    // the page that lists what the customer owns. `fullyRefundedProductIds`
    // asks the safe question, "is there nothing of this product left", and is
    // already trusted for exactly this judgement on the removal path above.
    // Same correction as the removal rule above: the `displayStatus !==
    // "active"` term here could never be false either. `fullyRefundedProductIds`
    // is what asks the safe question -- "is there nothing of this product left"
    // -- and a product with any surviving purchase is not in it, so a live row
    // cannot be relabelled by a refund on an earlier purchase.
    if (
      productId &&
      !paymentIntent &&
      fullyRefundedProductIds.has(productId)
    ) {
      return { ...row, displayStatus: "refunded" as const };
    }
    return row;
  });

  const removedCount =
    activeResult.length + expiredEntries.length - ownedRows.length;
  if (removedCount > 0) {
    logger.info(
      `[Subscriptions] SU-23: ${removedCount} refunded purchase(s) removed from My Products.`,
    );
  }

  return withSafetyNet as SubscriptionProduct[];
};
